// Quote paste (#657): with the caret on a quote line ("> words"), a paste of several lines keeps every pasted line in the
// quote, so the whole paste is one quote and not a first quoted line and loose text under it. One rule for every client;
// the cases are shared/cases/quote-paste.json, which the Swift and Kotlin ports run too; the web imports this file.

/**
 * The caret's line is a quote line to paste into: it starts with "> ", or it is a lone ">" that the first pasted line will
 * turn into "> " (the renderer reads a quote line only as "> " with a space: `quoteLine` in markdown.ts).
 */
const QUOTE_LINE = /^>( |$)/;

/**
 * The text to put in the box for a paste. `lineBeforeCaret` is the text from the start of the caret's line to the caret (to
 * the start of the selection, when text is selected and the paste replaces it); `pasted` is the clipboard text.
 *
 * - The line before the caret is not a quote line ("> words" or a lone ">"), or the paste is one line: `pasted` as it is.
 * - Otherwise every pasted line after the first gets "> " before it, empty lines too, so the paste stays one quote. Line
 *   breaks come out as "\n". The empty lines at the end of the paste stay plain: the caret ends on a fresh line, and the
 *   server trims the message, so a "> " there would send as a stray ">" under the quote.
 * - A pasted line that already starts with "> " keeps its marker: no "> > ". A pasted lone ">" becomes "> ", because the
 *   renderer would not read ">" as a quote line and the quote would split in two. When the caret follows a bare marker
 *   ("> " or ">"), a first pasted line that is a quote line gives up its own marker too.
 * - After a lone ">" the first line gets a space, so it is a quote line: ">" and "a\nb" give " a\n> b".
 */
export function quotePaste(lineBeforeCaret: string, pasted: string): string {
  if (!/[\r\n]/.test(pasted) || !QUOTE_LINE.test(lineBeforeCaret)) return pasted;
  const lines = pasted.split(/\r\n|\r|\n/);
  // The first index of the run of empty lines that ends the paste (never the first line).
  let plainFrom = lines.length;
  while (plainFrom > 1 && lines[plainFrom - 1] === "") plainFrom--;
  const bare = lineBeforeCaret === ">" || lineBeforeCaret === "> ";
  return lines
    .map((line, i) => {
      if (i === 0) {
        const first = bare && QUOTE_LINE.test(line) ? line.slice(line.startsWith("> ") ? 2 : 1) : line;
        return (lineBeforeCaret === ">" ? " " : "") + first;
      }
      if (i >= plainFrom) return "";
      if (line.startsWith("> ")) return line;
      return line === ">" ? "> " : `> ${line}`;
    })
    .join("\n");
}

/** The caret's line up to the caret: the text after the last line break before `caret` (UTF-16 offset, like a textarea's). */
export function lineBeforeCaret(text: string, caret: number): string {
  return text.slice(text.lastIndexOf("\n", caret - 1) + 1, caret);
}
